import { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'
import { createEvent, deleteEvent, getEvents, updateEvent } from '../../../actions/guk'
import {
  ClipboardIcon,
  PlusIcon,
  SearchIcon,
  ChevronDownIcon,
  SortIcon
} from '../../../assets/svg'
import { useOnClickOutside } from '../../../hooks'
import { formatRange } from '../../../utils/smallHelpers'
import Loader from '../../../components/Loader'
import { AddEditModal, EventCard } from './components'


const TABS = [
  { label: 'Все',          status: null },
  { label: 'Планируется',  status: 'planned' },
  { label: 'Приём заявок', status: 'accepting' },
  { label: 'Проводится',   status: 'in_progress' },
  { label: 'Завершено',    status: 'finished' }
]

const currentYear = new Date().getFullYear()
const YEAR_OPTIONS = Array.from({ length: 6 }, (_, i) => currentYear - 1 + i)

const mapEvent = (event) => ({
  id: event.id,
  title: event.name,
  status: event.status,
  dates: formatRange(event.start_date, event.end_date),
  place: event.location,
  responsible: event.organizer_name,
  commission: (event.commission || []).map((c) => c.name_snapshot),
  hasResults: false
})

const GukMain = () => {

  const dispatch = useDispatch()
  const { events: list, loading } = useSelector(state => state.guk)
  const events = list || []


  const [searchParams, setSearchParams] = useSearchParams()
  const status = searchParams.get('status')
  const yearParam = searchParams.get('year')
  const year = yearParam ? Number(yearParam) : null
  const activeTab = TABS.find((t) => t.status === status) || TABS[0]

  const [search, setSearch] = useState('')
  const [yearOpen, setYearOpen] = useState(false)
  const [modal, setModal] = useState({ open: false, mode: 'add', data: null })

  const yearRef = useRef(null)
  useOnClickOutside(yearRef, () => setYearOpen(false))

  const setFilter = (key, value) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (value === null || value === undefined || value === '') {
        next.delete(key)
      } else {
        next.set(key, String(value))
      }
      return next
    })
  }

  const filterParams = useMemo(() => {
    const params = {}
    if (status) params.status = status
    if (year) params.year = year
    return params
  }, [status, year])

  useEffect(() => {
    dispatch(getEvents(filterParams))
  }, [dispatch, filterParams])

  const openAddModal = () => setModal({ open: true, mode: 'add', data: null })
  const openEditModal = (event) => setModal({ open: true, mode: 'edit', data: event })
  const closeModal = () => setModal((m) => ({ ...m, open: false }))

  const reload = () => dispatch(getEvents(filterParams))

  const handleSave = async (form) => {
    if (modal.mode === 'add') {
      await dispatch(createEvent(form))
    } else {
      await dispatch(updateEvent(modal.data.id, form))
    }
    closeModal()
    reload()
  }

  const handleDelete = async (id) => {
    await dispatch(deleteEvent(id))
    reload()
  }

  return (
    <>
      <section className="flex flex-col gap-[1.25rem] lg:gap-[1.5rem] rounded-[16px] border border-solid border-[#d9d9d9] bg-white p-[1.25rem] lg:p-[1.5rem] shadow-[0px_1px_1px_rgba(0,0,0,0.05)]">
        <div className="flex flex-col gap-[1rem] sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-[0.625rem]">
            <div className="flex h-[2.25rem] w-[2.25rem] sm:h-[2.5rem] sm:w-[2.5rem] items-center justify-center rounded-[10px] bg-[#0d9488] shrink-0">
              <ClipboardIcon />
            </div>
            <h1 className="m-0 text-[1.375rem] sm:text-[1.625rem] lg:text-[2rem] font-semibold text-[#111827]">
              План мероприятий
            </h1>
          </div>
          <button
            type="button"
            onClick={openAddModal}
            className="flex items-center justify-center gap-[0.5rem] rounded-[10px] bg-[#0d9488] px-[1rem] sm:px-[1.5rem] py-[0.75rem] sm:py-[1rem] text-[0.938rem] sm:text-[1rem] font-medium text-white transition-colors hover:bg-[#0b8278]"
          >
            <PlusIcon />
            <span className="whitespace-nowrap">Добавить мероприятие</span>
          </button>
        </div>

        <div className="flex flex-wrap items-stretch gap-[0.625rem]">
          <label className="flex w-full lg:w-[31.25rem] items-center gap-[0.625rem] rounded-[10px] border border-solid border-[#d9d9d9] bg-[#f9fafb] px-[1rem] sm:px-[1.5rem] py-[0.75rem] sm:py-[1rem]">
            <SearchIcon />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Поиск по названию, месту, ответственному..."
              className="flex-1 min-w-0 bg-transparent text-[0.938rem] sm:text-[1rem] text-[#111827] placeholder:text-[#9ca3af] outline-none"
            />
          </label>

          <div className="flex h-[3.25rem] items-center gap-[0.25rem] sm:gap-[0.625rem] overflow-x-auto rounded-[10px] border border-solid border-[#d9d9d9] bg-[#f9fafb] px-[0.5rem] sm:px-[1rem] max-w-full">
            {TABS.map((tab) => {
              const isActive = activeTab.label === tab.label
              return (
                <button
                  key={tab.label}
                  type="button"
                  onClick={() => setFilter('status', tab.status)}
                  className={`flex h-[2.25rem] shrink-0 items-center justify-center rounded-[8px] px-[0.75rem] sm:px-[1rem] py-[0.5rem] text-[0.812rem] sm:text-[0.875rem] font-medium transition-colors ${
                    isActive
                      ? 'bg-white text-[#111827] shadow-[0px_1px_2px_rgba(0,0,0,0.05)]'
                      : 'text-[#6b7280] hover:text-[#111827]'
                  }`}
                >
                  {tab.label}
                </button>
              )
            })}
          </div>

          <div ref={yearRef} className="relative flex-1 sm:flex-none">
            <button
              type="button"
              onClick={() => setYearOpen((v) => !v)}
              className="flex h-[3.25rem] w-full sm:w-[11.312rem] items-center justify-between rounded-[12px] border border-solid border-[#d9d9d9] bg-[#f9fafb] px-[0.812rem]"
            >
              <span className="text-[0.875rem] text-[#111827]">
                {year ? `${year} год` : 'По дате'}
              </span>
              <ChevronDownIcon />
            </button>
            {yearOpen && (
              <div className="absolute right-0 top-[3.5rem] z-10 flex w-full sm:w-[11.312rem] flex-col gap-[0.125rem] rounded-[12px] border border-solid border-[#e5e7eb] bg-white p-[0.5rem] shadow-[0px_8px_24px_rgba(0,0,0,0.08)]">
                <button
                  type="button"
                  onClick={() => { setFilter('year', null); setYearOpen(false) }}
                  className={`rounded-[8px] px-[0.875rem] py-[0.5rem] text-left text-[0.875rem] hover:bg-[#f3f4f6] ${
                    year === null ? 'bg-[#f3f4f6] text-[#111827]' : 'text-[#374151]'
                  }`}
                >
                  Все годы
                </button>
                {YEAR_OPTIONS.map((y) => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => { setFilter('year', y); setYearOpen(false) }}
                    className={`rounded-[8px] px-[0.875rem] py-[0.5rem] text-left text-[0.875rem] hover:bg-[#f3f4f6] ${
                      year === y ? 'bg-[#f3f4f6] text-[#111827]' : 'text-[#374151]'
                    }`}
                  >
                    {y}
                  </button>
                ))}
              </div>
            )}
          </div>

          <button
            type="button"
            className="flex h-[3.25rem] w-[3.25rem] items-center justify-center rounded-[12px] border border-solid border-[#d9d9d9] bg-[#f9fafb] shrink-0"
          >
            <SortIcon />
          </button>
        </div>

        <p className="m-0 flex items-center gap-[0.125rem] text-[0.812rem] sm:text-[0.875rem]">
          <span className="text-[#6b7280]">Показано:&nbsp;</span>
          <span className="font-semibold text-[#111827]">{events.length}</span>
          <span className="text-[#6b7280]">&nbsp;из&nbsp;</span>
          <span className="font-semibold text-[#111827]">{events.length}</span>
          <span className="text-[#6b7280]">&nbsp;мероприятий</span>
        </p>
      </section>

      {loading ? (
        <Loader size="lg" color="rgb(218 35 41)" />
      ) : (
        <div className="grid grid-cols-1 gap-[1.25rem] sm:grid-cols-2 xl:grid-cols-3">
          {events.map((event) => (
            <EventCard
              key={event.id}
              {...mapEvent(event)}
              onEdit={() => openEditModal(event)}
              onDelete={() => handleDelete(event.id)}
            />
          ))}
        </div>
      )}

      <AddEditModal
        open={modal.open}
        mode={modal.mode}
        initialData={modal.data}
        onClose={closeModal}
        onSave={handleSave}
      />
    </>
  )
}

export default GukMain
